import { Button, ErrorNotification } from '@mr-yum/frontend-ui'
import { Sheet } from 'components/Common/Sheet'
import { TextareaField } from 'components/HookForm/TextareaField'
import { useLogger } from 'hooks/useLogger'
import { useSubmitReview } from 'hooks/useSubmitReview'
import { Review } from 'lib/gql'
import React, { useCallback } from 'react'
import {
  Controller,
  SubmitHandler,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { ReviewMessage } from './ReviewMessage'

export interface GuestReviewFormValues {
  notes: Review['notes']
  rating: Review['rating']
}

export interface GuestReviewDrawerProps {
  orderId: string
  reviewId: string | null
  onDone: () => void
  onClose: () => void
}

export const GuestReviewSheet = ({
  orderId,
  reviewId,
  onDone,
  onClose,
}: GuestReviewDrawerProps) => {
  const { submitReview } = useSubmitReview(orderId, reviewId)
  const { logEvent } = useLogger()
  const {
    handleSubmit,
    setError,
    control,
    formState: { isSubmitting, errors },
  } = useFormContext<GuestReviewFormValues>()

  const rating = useWatch({ control, name: 'rating' })
  const onSubmit = useCallback<SubmitHandler<GuestReviewFormValues>>(
    async ({ notes, rating }) => {
      logEvent('Submit Review', {
        rating,
      })

      const { error } = await submitReview({ notes, rating })

      if (error) {
        setError('root.serverError', {
          type: '400',
          message: error.graphQLErrors[0]?.message,
        })
      } else {
        onDone()
      }
    },
    [logEvent, onDone, submitReview, setError],
  )

  return (
    <Sheet
      showBack={false}
      isCloseable
      onClose={onClose}
      title={<ReviewMessage rating={rating} />}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="p-4">
        {errors.root && (
          <ErrorNotification
            fullWidth
            error={new Error('Something went wrong, please try again.')}
          />
        )}

        <Controller
          name="notes"
          render={({ field }) => (
            <TextareaField
              className={{
                root: 'mb-5',
                input:
                  'h-28 resize-none bg-surface-subtle my-body-md placeholder:text-[theme(backgroundColor.interactive-surface-disabled)] focus:bg-surface',
              }}
              max={240}
              name="notes"
              autoFocus
              data-testid="notes"
              placeholder="Leave your thoughts here..."
              onClear={() => field.onChange('')}
            />
          )}
        />
        <Button
          size="lg"
          data-testid="review-submit-button"
          type="submit"
          isLoading={isSubmitting}
          fullWidth
        >
          <FormattedMessage defaultMessage="Submit Review" id="VB5kFQ" />
        </Button>
      </form>
    </Sheet>
  )
}
